"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useLenis } from "./providers/lenis";
import dynamic from "next/dynamic";

const PilotModal = dynamic(() => import("./pilot-modal"), {
  loading: () => null,
  ssr: false,
});

const Footer = () => {
  const [isCtaVisible, setIsCtaVisible] = useState(false);
  const [isPilotModalOpen, setIsPilotModalOpen] = useState(false);
  const ctaRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsCtaVisible(true);
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const { scrollTo } = useLenis();

  const handleScrollTo = (target: string) => {
    scrollTo(target, { duration: 2 });
  };
  
  return (
    <>
      <footer className="w-full bg-surface-white text-surface-white">
        {/* Top CTA Section */}
        <section
          ref={ctaRef}
          className="relative overflow-hidden py-14.5 lg:py-[70px] xl:py-[83px] flex flex-col items-center justify-center text-center px-4"
        >
          <div
            className={`absolute -right-[115px] -top-[103px] size-[185px] md:size-[225px] lg:size-[270px] xl:size-[338px] bg-primary/15 rounded-full pointer-events-none transform transition-transform duration-[2500ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
              isCtaVisible ? "scale-100 opacity-40" : "scale-0 opacity-0"
            }`}
            aria-hidden="true"
          />
          <div
            className={`absolute z-15 -left-[115px] -bottom-[103px] bg-primary/15 size-[185px] md:size-[225px] lg:size-[270px] xl:size-[338px] rounded-full pointer-events-none transform transition-transform duration-[3000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
              isCtaVisible ? "scale-100 opacity-40" : "scale-0 opacity-0"
            }`}
            style={{ transitionDelay: "400ms" }}
            aria-hidden="true"
          />

          <div
            className={` relative z-10 mx-auto space-y-4.5 md:space-y-4 lg:space-y-6 xl:space-y-8 transition-all duration-[1500ms] ease-out ${
              isCtaVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "600ms" }}
          >
            <div className="space-y-2.75 lg:space-y-4 xl:space-y-6 text-center text-surface-black">
              <div className="overflow-hidden">
                <h2
                  className={`text-[22px] sm:text-[28px] lg:text-[37px] xl:text-5xl px-1 md:px-4 w-full md:w-[640px] lg:w-[790px] xl:w-[1054px] font-semibold leading-6.75 md:leading-8.5 lg:leading-12 xl:leading-16 tracking-normal transition-transform duration-[2500ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                    isCtaVisible ? "translate-y-0" : "translate-y-[120%]"
                  }`}
                  style={{ transitionDelay: "700ms" }}
                >
                  You Don't Have to Navigate Pregnancy and Postpartum Alone.
                </h2>
              </div>

              <p className="text-sm xl:text-base md:max-w-[390px] xl:max-w-[446px] mx-auto">
                Join BrimmHQ for guided support, trusted information, and a more
                structured maternal journey.
              </p>
            </div>

            <div className="space-y-4.5 md:space-y-3 lg:space-y-4 xl:space-y-6">
              <button
                onClick={() => setIsPilotModalOpen(true)}
                className="w-auto group cursor-pointer font-semibold text-base sm:text-[17px] lg:text-lg xl:text-2xl text-surface-white hover:text-primary active:text-primary bg-primary hover:bg-white active:bg-white outline-1 outline-transparent hover:outline-primary active:outline-primary transition-colors duration-300 rounded-[100px] py-3 md:py-2.5 lg:py-3.5 xl:py-4.5 px-6 md:px-3.5 lg:px-4.5 xl:px-6 inline-flex items-center justify-center gap-x-3 sm:gap-x-2"
              >
                <span>Join the Pilot</span>
                <div className="relative overflow-hidden shrink-0 size-6.5 sm:size-6 xl:size-7.5 rounded-full">
                  {/* Arrow 1: Visible by default, slides right on hover/active */}
                  <svg
                    className="absolute inset-0 transition-transform duration-300 group-hover:translate-x-[150%] group-active:translate-x-[150%]"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="30" height="30" rx="15" fill="#3375D6" />
                    <mask
                      id="mask0_btn_ftr_1"
                      maskUnits="userSpaceOnUse"
                      x="3"
                      y="3"
                      width="24"
                      height="24"
                    >
                      <rect x="3" y="3" width="24" height="24" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_btn_ftr_1)">
                      <path
                        d="M19.175 16H7V14H19.175L13.575 8.4L15 7L23 15L15 23L13.575 21.6L19.175 16Z"
                        fill="white"
                      />
                    </g>
                  </svg>

                  {/* Arrow 2: Hidden by default (left), slides in on hover/active */}
                  <svg
                    className="absolute inset-0 transition-transform duration-300 -translate-x-[150%] group-hover:translate-x-0 group-active:translate-x-0"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="30" height="30" rx="15" fill="#3375D6" />
                    <mask
                      id="mask0_btn_ftr_2"
                      maskUnits="userSpaceOnUse"
                      x="3"
                      y="3"
                      width="24"
                      height="24"
                    >
                      <rect x="3" y="3" width="24" height="24" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_btn_ftr_2)">
                      <path
                        d="M19.175 16H7V14H19.175L13.575 8.4L15 7L23 15L15 23L13.575 21.6L19.175 16Z"
                        fill="white"
                      />
                    </g>
                  </svg>
                </div>
              </button>

              <ul className="flex flex-col sm:flex-row items-center justify-center gap-3.75 md:gap-4 lg:gap-6 xl:gap-8 text-sm xl:text-base text-surface-black/80 font-medium">
                {[
                  "Expert-backed support",
                  "Privacy-first approach",
                  "Limited pilot spot",
                ].map((item, idx) => (
                  <li
                    key={idx}
                    className={`flex items-center gap-x-2 transition-opacity duration-1000 ${
                      isCtaVisible ? "opacity-100" : "opacity-0"
                    }`}
                    style={{ transitionDelay: `${1100 + idx * 300}ms` }}
                  >
                    <svg
                      className="size-5 text-surface-black shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Main Footer Section */}
        <section className="bg-primary pt-12 xl:pt-16 pb-16 lg:pb-20 xl:pb-26 text-surface-white">
          <div className="2xl:container 2xl:mx-auto px-4 md:pl-7.5 xl:pl-10.25 md:pr-14.5 xl:pr-18.5 text-sm xl:text-base">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-5 xl:gap-8 pb-12 xl:pb-16 border-b border-white/20">
              {/* Logo and Description */}
              <div className="lg:col-span-4 md:pr-8 xl:pr-12 space-y-3 xl:space-y-4">
                <div className="flex items-center gap-x-2">
                  {/* Logo Icon */}
                  <div className="size-10 md:size-12 xl:size-14.5 relative">
                    <Image
                      src={"/images/BrimmLogoFooter.png"}
                      alt="Brimm HQ Logo"
                      quality={100}
                      fill
                      className="object-contain object-center"
                    />
                  </div>
                </div>

                <h3 className="leading-6 md:max-w-sm">
                  BRIMMHQ is a maternal support venture focused on pregnancy and
                  postpartum guidance, support, and continuity.
                </h3>
              </div>

              {/* Links Column 1 */}
              <div className="lg:col-span-2 space-y-3 md:space-y-4 xl:space-y-6">
                <h4 className="font-semibold tracking-wide">Links</h4>

                <ul className="space-y-3 xl:space-y-4 ">
                  <li>
                    <a
                      href="#"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                      onClick={() => handleScrollTo("#home")}
                    >
                      Home
                    </a>
                  </li>

                  <li>
                    <a
                      href="#"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                      onClick={() => handleScrollTo("#about")}
                    >
                      About
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                      onClick={() => handleScrollTo("#how-it-works")}
                    >
                      How it Works
                    </a>
                  </li>
                </ul>
              </div>

              {/* Links Column 2 */}
              <div className="lg:col-span-3 space-y-3 md:space-y-4 xl:space-y-6">
                <h4 className="font-semibold tracking-wide">Resources</h4>
                <ul className="space-y-3 xl:space-y-4 ">
                  <li>
                    <a
                      href="#"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                      onClick={() => handleScrollTo("#blog")}
                    >
                      Brimm HQ Blog
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                    >
                      Community Forum
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                      onClick={() => handleScrollTo("#faq")}
                    >
                      FAQ
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                      onClick={() => handleScrollTo("#testimonials")}
                    >
                      Testimonials
                    </a>
                  </li>
                </ul>
              </div>

              {/* Links Column 3 & Socials */}
              <div className="lg:col-span-3 space-y-3 md:space-y-4 xl:space-y-6">
                <h4 className="font-semibold tracking-wide">Contact</h4>
                <ul className="space-y-3 xl:space-y-4  tracking-wide">
                  <li>
                    <a
                      href="mailto:Brimmhq@gmail.com"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                    >
                      Brimmhq@gmail.com
                    </a>
                  </li>
                  <li>
                    <a
                      href="tel:+2348135353183"
                      className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                    >
                      +234 8135353183
                    </a>
                  </li>
                  <li className="">Lagos, Nigeria</li>
                </ul>

                {/* Social Icons */}
                <div className="flex items-center gap-x-5 md:gap-x-3 xl:gap-x-4 pt-3 xl:pt-4">
                  {/* Slack */}
                  <a
                    href="https://app.slack.com/client/T0ADHV9F46N"
                    aria-label="Slack"
                    target="_blank"
                    className="hover: transition-colors relative shrink-0 w-14 md:w-13.5 xl:w-17.5 h-5 md:h-4.25 xl:h-6.25"
                  >
                    <Image
                      src={"/images/socials/slack.svg"}
                      alt="Slack Icon"
                      fill
                      quality={100}
                      className="object-contain object-center"
                    />
                  </a>
                  {/* Facebook */}
                  <a
                    href="https://www.facebook.com/share/18fDhTQkcr/?mibextid=wwXIfr"
                    aria-label="Facebook"
                    target="_blank"
                    className="hover: transition-colors"
                  >
                    <svg
                      fill="inherit"
                      className="size-7.5 xl:size-10 fill-surface-white"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path d="M12 2.03998C6.5 2.03998 2 6.52998 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.84998C10.44 7.33998 11.93 5.95998 14.22 5.95998C15.31 5.95998 16.45 6.14998 16.45 6.14998V8.61998H15.19C13.95 8.61998 13.56 9.38998 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96C15.9164 21.5878 18.0622 20.3855 19.6099 18.57C21.1576 16.7546 22.0054 14.4456 22 12.06C22 6.52998 17.5 2.03998 12 2.03998Z"></path>{" "}
                      </g>
                    </svg>
                  </a>
                  {/* LinkedIn */}
                  <a
                    href="https://www.linkedin.com/company/brimmhq/"
                    aria-label="LinkedIn"
                    className="hover: transition-colors"
                    target="_blank"
                  >
                    <svg
                      className="size-7.5 xl:size-10"
                      viewBox="0 0 40 34"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_362_17)">
                        <path
                          d="M38.0372 31.5866C38.123 31.5874 38.2081 31.5708 38.2872 31.5377C38.3662 31.5047 38.4376 31.4559 38.4968 31.3944C38.556 31.3329 38.6019 31.26 38.6315 31.1802C38.6611 31.1004 38.6737 31.0155 38.6688 30.9306C38.6688 30.4654 38.3854 30.2432 37.8038 30.2432H36.8642V32.6787H37.2176V31.6171H37.6519L37.6618 31.63L38.3358 32.6787H38.7137L37.9886 31.5933L38.0372 31.5866ZM37.6284 31.3426H37.2186V30.5192H37.738C38.0064 30.5192 38.3123 30.5626 38.3123 30.9105C38.3123 31.3105 38.0027 31.3426 37.6263 31.3426"
                          fill="white"
                        />
                        <path
                          d="M28.8181 28.5345H23.8067V20.7636C23.8067 18.9106 23.7733 16.5251 21.2002 16.5251C18.59 16.5251 18.1907 18.5441 18.1907 20.6287V28.534H13.1792V12.5539H17.9902V14.7377H18.0576C18.539 13.9226 19.2348 13.2521 20.0707 12.7975C20.9066 12.3429 21.8514 12.1214 22.8044 12.1564C27.8837 12.1564 28.8202 15.4645 28.8202 19.7681L28.8181 28.5345ZM7.52456 10.3695C6.94936 10.3696 6.38704 10.2008 5.90873 9.8845C5.43042 9.56817 5.05761 9.11851 4.8374 8.59237C4.61719 8.06623 4.55947 7.48726 4.67159 6.92865C4.7837 6.37005 5.0606 5.85691 5.46725 5.45412C5.8739 5.05133 6.39205 4.77698 6.95617 4.66577C7.52029 4.55456 8.10503 4.61148 8.63648 4.82934C9.16793 5.04719 9.6222 5.41619 9.94184 5.88968C10.2615 6.36317 10.4322 6.91988 10.4323 7.48941C10.4323 7.86756 10.3572 8.24202 10.2111 8.59141C10.065 8.94081 9.85085 9.25829 9.58085 9.52573C9.31084 9.79317 8.99026 10.0053 8.63744 10.1501C8.28462 10.2949 7.90647 10.3694 7.52456 10.3695ZM10.0303 28.5345H5.0136V12.5539H10.0303V28.5345ZM31.3166 0.00228163H2.49534C1.84118 -0.00502787 1.21082 0.245113 0.742824 0.697737C0.274832 1.15036 0.00749539 1.76844 -0.000488281 2.41615V31.0719C0.00722215 31.7199 0.274402 32.3384 0.742378 32.7915C1.21035 33.2446 1.84085 33.4953 2.49534 33.4884H31.3166C31.9724 33.4965 32.6046 33.2466 33.0744 32.7935C33.5443 32.3404 33.8132 31.7212 33.8223 31.0719V2.41408C33.813 1.76509 33.5438 1.14631 33.074 0.693693C32.6041 0.24108 31.972 -0.00834623 31.3166 0.000213241"
                          fill="white"
                        />
                        <path
                          d="M37.6712 29.1582C37.0615 29.164 36.4789 29.4087 36.0509 29.8386C35.6228 30.2686 35.3842 30.849 35.3871 31.4527C35.39 32.0564 35.6343 32.6345 36.0665 33.0603C36.4987 33.4862 37.0837 33.7253 37.6934 33.7253C38.3032 33.7253 38.8881 33.4862 39.3203 33.0603C39.7525 32.6345 39.9968 32.0564 39.9997 31.4527C40.0027 30.849 39.764 30.2686 39.336 29.8386C38.9079 29.4087 38.3253 29.164 37.7156 29.1582H37.6712ZM37.6712 33.4639C37.2713 33.4705 36.8784 33.3595 36.5422 33.145C36.206 32.9305 35.9415 32.622 35.7823 32.2587C35.6231 31.8954 35.5763 31.4935 35.6478 31.1038C35.7193 30.7142 35.9059 30.3542 36.184 30.0696C36.4621 29.7849 36.8191 29.5883 37.2101 29.5046C37.601 29.4208 38.0083 29.4538 38.3803 29.5992C38.7523 29.7447 39.0724 29.9961 39.3002 30.3217C39.5279 30.6473 39.653 31.0324 39.6596 31.4284C39.6596 31.4397 39.6596 31.4506 39.6596 31.462C39.6708 31.9814 39.4732 32.484 39.1102 32.8593C38.7472 33.2345 38.2486 33.4516 37.7239 33.4628H37.6718"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_362_17">
                          <rect width="40" height="33.7662" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                  {/* Instagram */}
                  <a
                    href="https://www.instagram.com/brimmhq"
                    aria-label="Instagram"
                    className="hover: transition-colors"
                    target="_blank"
                  >
                    <svg
                      className="size-7.5 xl:size-10"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_362_21)">
                        <path
                          d="M11.7168 0.139875C9.58877 0.240275 8.13557 0.579875 6.86517 1.07908C5.55037 1.59148 4.43597 2.27908 3.32717 3.39188C2.21837 4.50468 1.53557 5.61988 1.02677 6.93668C0.534371 8.20988 0.200771 9.66428 0.106771 11.7935C0.0127714 13.9227 -0.00802861 14.6071 0.00237139 20.0383C0.0127714 25.4695 0.0367714 26.1503 0.139971 28.2839C0.241571 30.4115 0.579971 31.8643 1.07917 33.1351C1.59237 34.4499 2.27917 35.5639 3.39237 36.6731C4.50557 37.7823 5.61997 38.4635 6.93997 38.9731C8.21197 39.4647 9.66677 39.7999 11.7956 39.8931C13.9244 39.9863 14.6096 40.0079 20.0392 39.9975C25.4688 39.9871 26.1524 39.9631 28.2856 39.8619C30.4188 39.7607 31.864 39.4199 33.1352 38.9231C34.45 38.4087 35.5648 37.7231 36.6732 36.6095C37.7816 35.4959 38.464 34.3799 38.9724 33.0623C39.4652 31.7903 39.8 30.3355 39.8924 28.2083C39.9856 26.0735 40.0076 25.3919 39.9972 19.9615C39.9868 14.5311 39.9624 13.8503 39.8612 11.7175C39.76 9.58468 39.4212 8.13628 38.9224 6.86468C38.4084 5.54988 37.7224 4.43668 36.6096 3.32668C35.4968 2.21668 34.38 1.53468 33.0628 1.02748C31.79 0.535075 30.336 0.199475 28.2072 0.107475C26.0784 0.0154752 25.3932 -0.00812478 19.9616 0.00227522C14.53 0.0126752 13.85 0.0358752 11.7168 0.139875ZM11.9504 36.2951C10.0004 36.2103 8.94157 35.8863 8.23597 35.6151C7.30157 35.2551 6.63597 34.8199 5.93277 34.1235C5.22957 33.4271 4.79757 32.7591 4.43277 31.8267C4.15877 31.1211 3.82877 30.0635 3.73757 28.1135C3.63837 26.0059 3.61757 25.3731 3.60597 20.0335C3.59437 14.6939 3.61477 14.0619 3.70717 11.9535C3.79037 10.0051 4.11637 8.94508 4.38717 8.23988C4.74717 7.30428 5.18077 6.63988 5.87877 5.93708C6.57677 5.23428 7.24277 4.80148 8.17597 4.43668C8.88077 4.16148 9.93837 3.83428 11.8876 3.74148C13.9968 3.64148 14.6288 3.62148 19.9676 3.60988C25.3064 3.59828 25.94 3.61828 28.05 3.71108C29.9984 3.79588 31.0588 4.11868 31.7632 4.39108C32.698 4.75108 33.3632 5.18348 34.066 5.88268C34.7688 6.58188 35.202 7.24548 35.5668 8.18068C35.8424 8.88348 36.1696 9.94068 36.2616 11.8911C36.362 14.0003 36.3848 14.6327 36.3944 19.9711C36.404 25.3095 36.3852 25.9435 36.2928 28.0511C36.2076 30.0011 35.8844 31.0603 35.6128 31.7667C35.2528 32.7007 34.8188 33.3667 34.1204 34.0691C33.422 34.7715 32.7568 35.2043 31.8232 35.5691C31.1192 35.8439 30.0604 36.1719 28.1128 36.2647C26.0036 36.3639 25.3716 36.3847 20.0308 36.3963C14.69 36.4079 14.06 36.3863 11.9508 36.2951M28.2548 9.31068C28.2556 9.78538 28.3971 10.2492 28.6615 10.6435C28.9259 11.0377 29.3013 11.3447 29.7402 11.5256C30.1791 11.7065 30.6618 11.7532 31.1272 11.6597C31.5926 11.5663 32.0199 11.3369 32.3549 11.0006C32.69 10.6643 32.9178 10.2362 33.0095 9.77047C33.1013 9.30471 33.0528 8.82221 32.8703 8.38398C32.6878 7.94576 32.3794 7.5715 31.9842 7.30854C31.589 7.04558 31.1247 6.90573 30.65 6.90668C30.0136 6.90795 29.4038 7.16191 28.9546 7.61273C28.5054 8.06354 28.2537 8.67429 28.2548 9.31068ZM9.73077 20.0199C9.74197 25.6919 14.3484 30.2795 20.0192 30.2687C25.69 30.2579 30.2808 25.6519 30.27 19.9799C30.2592 14.3079 25.6516 9.71908 19.98 9.73028C14.3084 9.74148 9.71997 14.3487 9.73077 20.0199ZM13.3332 20.0127C13.3306 18.6941 13.719 17.4044 14.4494 16.3066C15.1798 15.2088 16.2193 14.3522 17.4365 13.8452C18.6537 13.3382 19.9939 13.2035 21.2876 13.4582C22.5814 13.7129 23.7705 14.3455 24.7048 15.276C25.639 16.2065 26.2763 17.3932 26.5361 18.6859C26.7959 19.9786 26.6665 21.3194 26.1643 22.5386C25.6621 23.7578 24.8097 24.8007 23.7148 25.5354C22.6199 26.2701 21.3317 26.6637 20.0132 26.6663C19.1376 26.6681 18.2703 26.4975 17.4608 26.1641C16.6512 25.8307 15.9152 25.3411 15.2949 24.7232C14.6746 24.1053 14.1821 23.3713 13.8455 22.5631C13.5089 21.7548 13.3348 20.8882 13.3332 20.0127Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_362_21">
                          <rect width="40" height="40" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/2348135353183"
                    aria-label="WhatsApp"
                    className="hover: transition-colors"
                    target="_blank"
                  >
                    <svg
                      className="size-7.5 xl:size-10"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M34.1718 5.81258C30.4121 2.06615 25.4118 0.00190078 20.084 0C9.10567 0 0.17094 8.8909 0.16712 19.8194C0.16521 23.313 1.08294 26.723 2.82577 29.7282L0 40L10.5582 37.2438C13.467 38.8235 16.7426 39.655 20.0754 39.656H20.084C31.0604 39.656 39.9962 30.7641 40 19.8356C40.0019 14.5391 37.9324 9.55998 34.1718 5.81353V5.81258ZM20.084 36.3087H20.0773C17.1073 36.3078 14.1938 35.5132 11.6517 34.0125L11.0471 33.6553L4.78156 35.2908L6.45371 29.2111L6.06026 28.5877C4.40338 25.9647 3.52767 22.9329 3.52958 19.8204C3.5334 10.7375 10.9593 3.34727 20.0908 3.34727C24.5122 3.34917 28.6683 5.06463 31.7939 8.17906C34.9196 11.2925 36.6394 15.4325 36.6376 19.8337C36.6337 28.9176 29.2079 36.3078 20.084 36.3078V36.3087ZM29.1639 23.9707C28.6663 23.7227 26.2198 22.5252 25.7632 22.3598C25.3068 22.1944 24.9754 22.1118 24.644 22.6079C24.3127 23.104 23.3587 24.2198 23.0683 24.5495C22.778 24.8802 22.4877 24.9212 21.9901 24.673C21.4927 24.425 19.8892 23.9023 17.9879 22.2153C16.5087 20.9019 15.5097 19.2806 15.2194 18.7844C14.9291 18.2884 15.1889 18.0203 15.4371 17.7742C15.6606 17.5518 15.9347 17.1954 16.1839 16.9065C16.4332 16.6175 16.5153 16.4104 16.6814 16.0806C16.8477 15.7498 16.7646 15.461 16.6404 15.2129C16.5162 14.9648 15.5212 12.527 15.1058 11.5358C14.7018 10.5702 14.2912 10.7014 13.9866 10.6852C13.6962 10.671 13.3649 10.6681 13.0326 10.6681C12.7002 10.6681 12.1616 10.7917 11.7051 11.2878C11.2487 11.7838 9.96323 12.9823 9.96323 15.4191C9.96323 17.8559 11.7462 20.212 11.9954 20.5427C12.2447 20.8735 15.5049 25.8753 20.4966 28.0213C21.6837 28.5316 22.6109 28.8367 23.3338 29.0648C24.5257 29.4421 25.6104 29.3889 26.468 29.2616C27.424 29.119 29.4122 28.0631 29.8267 26.9065C30.2411 25.7498 30.2411 24.7576 30.117 24.5514C29.9929 24.3452 29.6606 24.2206 29.163 23.9726L29.1639 23.9707Z"
                        fill="white"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Footer Bottom Line */}
            <div className="pt-4 xl:pt-6 flex flex-col md:flex-row items-center justify-between gap-4/70 font-medium">
              <p>
                &copy; {new Date().getFullYear()} BRIMM HQ. All rights reserved
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 md:gap-y-2">
                <a
                  href="#"
                  className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                >
                  Privacy Policy
                </a>
                <a
                  href="#"
                  className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                >
                  Terms of Service
                </a>
                <a
                  href="#"
                  className="relative inline-block transition-colors after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[1.5px] after:bg-white after:transition-all after:duration-500 hover:after:w-full"
                >
                  Accessibility
                </a>
              </div>
            </div>
          </div>
        </section>
      </footer>

      <PilotModal
        isOpen={isPilotModalOpen}
        onClose={() => setIsPilotModalOpen(false)}
      />
    </>
  );
};

export default Footer;
